import React from 'react';
import { useWizard, WizardSubStep } from '@zillow/zrs-rental-applications';
import { Anchor, Flex, Heading, IconFaceUnhappy, Paragraph } from '@zillow/constellation';
import { CUSTOMER_SUPPORT_LINK, EXPERIAN_DISPUTE_URL } from '../../../constants/urls';
import { useIdVerificationCallbacks, useTermSet } from '../../../contexts/IdVerificationContext';
import { ExitReason, TermSet } from '../../../../../models';

interface UserLockedOutProps {
  id: string;
}

function LandlordContent() {
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <IconFaceUnhappy size="md" fontColor="blue600" />
      <Heading level={4} as="h2">
        Your account is temporarily locked
      </Heading>
      <Paragraph>
        For security, your account has been temporarily locked after three failed attempts to verify your identity.
        Please wait 24 hours before submitting again.
      </Paragraph>
      <Paragraph>
        During this time, you won’t be able to access applicant’s information, including credit and background reports.
      </Paragraph>
      <Paragraph>
        Accounts cannot be unlocked manually during the 24-hour waiting period. If you continue to experience issues
        with verifying your identity, please{' '}
        <Anchor href={CUSTOMER_SUPPORT_LINK} target="_blank" rel="noopener noreferrer">
          contact customer support
        </Anchor>
        .
      </Paragraph>
    </Flex>
  );
}

function RenterContent() {
  return (
    <Flex display="flex" flexDirection="column" gap="sm">
      <IconFaceUnhappy size="md" fontColor="blue600" />
      <Heading level={4} as="h2">
        Your account is temporarily locked
      </Heading>
      <Paragraph>
        For security, your account has been temporarily locked after three failed attempts to verify your identity.
        Please wait 24 hours before submitting again.
      </Paragraph>
      <Paragraph>During this time, you won’t be able to access the credit and background report services.</Paragraph>
      <Paragraph>
        Accounts cannot be unlocked manually during the 24-hour waiting period. If you continue to experience issues
        with verifying your identity, please{' '}
        <Anchor href={EXPERIAN_DISPUTE_URL} target="_blank" rel="noopener noreferrer">
          contact Experian
        </Anchor>
        .
      </Paragraph>
    </Flex>
  );
}

export default function UserLockedOut({ id }: UserLockedOutProps) {
  const { currentSubStep } = useWizard();
  const termSet = useTermSet();
  const { onExit } = useIdVerificationCallbacks();
  return (
    <WizardSubStep
      id={id}
      hidden={currentSubStep !== id}
      meta={{
        renderNextButton: 'Done',
        renderBackButton: () => null,
      }}
      onRequestNext={(abortController: AbortController) => {
        abortController.abort();
        onExit({ reason: ExitReason.UserLockedOut, success: false });
      }}
      render={termSet === TermSet.LandlordScreening ? LandlordContent : RenterContent}
    />
  );
}
